import { nextTick, shallowRef, watch } from 'vue'

// The catalog publishes readiness after its asynchronous content has loaded.
// Keeping scroll ownership here prevents component watchers overriding history.
export function createScrollPolicy() {
  const catalog = shallowRef(null)
  const pending = new Set()
  let version = 0

  function cancel() {
    version++
    for (const finish of [...pending]) finish(false)
  }

  function registerCatalog(getState) {
    catalog.value = getState
    return () => {
      if (catalog.value === getState) catalog.value = null
    }
  }

  function waitForCatalog(route, requestVersion) {
    return new Promise(resolve => {
      let stop = () => {}
      const finish = value => {
        stop()
        pending.delete(finish)
        resolve(value)
      }
      pending.add(finish)
      stop = watch(() => catalog.value?.(), async state => {
        if (!state?.ready || state.fullPath !== route.fullPath) return
        await nextTick()
        finish(requestVersion === version)
      }, { immediate: true, flush: 'post' })
    })
  }

  async function scrollBehavior(to, from, savedPosition) {
    cancel()
    const requestVersion = version
    if (to.name === 'products' && !await waitForCatalog(to, requestVersion)) return false
    if (requestVersion !== version) return false
    // Explicit instant behavior also avoids the global CSS smooth-scroll rule
    // animating history restoration or leaving an old animation in flight.
    if (savedPosition) return { ...savedPosition, behavior: 'instant' }
    const categoryId = Number(to.query.categoryId)
    if (to.name === 'products' && Number.isInteger(categoryId) && categoryId > 0) {
      const el = document.getElementById('catalog-products')
      if (el) {
        const top = (document.querySelector('.navbar-wrap')?.getBoundingClientRect().height || 0) + 16
        return { el, top, behavior: 'instant' }
      }
    }
    return { left: 0, top: 0, behavior: 'instant' }
  }

  async function scrollToProducts(route) {
    cancel()
    const requestVersion = version
    if (!await waitForCatalog(route, requestVersion) || requestVersion !== version) return
    const el = document.getElementById('catalog-products')
    if (!el) return
    const offset = (document.querySelector('.navbar-wrap')?.getBoundingClientRect().height || 0) + 16
    window.scrollTo({ left: 0, top: window.scrollY + el.getBoundingClientRect().top - offset, behavior: 'instant' })
  }

  return { cancel, registerCatalog, scrollBehavior, scrollToProducts }
}

export const catalogScrollPolicy = createScrollPolicy()
